import { z } from 'zod';

import { EventTypeSchema, UniformSchema } from './enums.js';
import type { EventType, Role } from './enums.js';
import { NonEmptyStringSchema, TimestampSchema, UuidSchema } from './primitives.js';

/**
 * Schedule events: games, practices, tournaments, and everything else that
 * puts a family in a car.
 *
 * BUILD_PLAN 4.5 says one thing about this module louder than the rest:
 * "Arrival time separate from start time matters more than anything else here.
 * It is the single most-asked question of the season." So `arrivalAt` is a
 * first-class field with its own ordering rule, not a note someone types into
 * the description and half the parents miss.
 */

/**
 * Which event types can name an opponent.
 *
 * A practice with an opponent is a data-entry error, and the cheapest place to
 * catch it is here rather than after it has been showing the wrong thing on a
 * parent's phone for a week. Tournaments are included because a tournament
 * bracket entry frequently does name one, even though the day holds several.
 */
export const OPPONENT_TYPES: ReadonlySet<EventType> = new Set<EventType>([
  'game',
  'scrimmage',
  'tournament',
]);

/**
 * Who may create, edit, or cancel a schedule event.
 *
 * BUILD_PLAN section 3: Head Coach and Assistant Coach both get "schedule
 * create and edit", and Team Manager's row leads with "Schedule". Scorekeeper
 * and Guardian read it and never write it. Owner is included because an owner
 * holds every capability in their own club.
 *
 * Exported as data rather than written as an `if` in the route, so a reviewer
 * checking rubric A against BUILD_PLAN section 3 compares two lists instead of
 * reading a handler.
 */
export const SCHEDULE_WRITE: ReadonlySet<Role> = new Set<Role>([
  'owner',
  'head_coach',
  'assistant_coach',
  'team_manager',
]);

export function canWriteSchedule(roles: readonly Role[]): boolean {
  return roles.some((role) => SCHEDULE_WRITE.has(role));
}

/**
 * Who may READ the schedule.
 *
 * BUILD_PLAN section 3, followed literally. It lists "Schedule" on Head Coach,
 * Assistant Coach and Team Manager, and "team schedule" on Guardian. It does
 * NOT list the schedule on the Scorekeeper row, whose grants are "Scorebook
 * write for assigned games only. Sees jersey numbers and names. No contact
 * info, no medical data, no messaging."
 *
 * A scorekeeper plausibly needs to know when the game is, and that reasoning is
 * exactly what CLAUDE.md forbids acting on: "If the plan is silent, ask. Do not
 * invent silently." That role is also handed to a stranger through a texted
 * link, so the safe reading and the literal reading agree. Raised as an open
 * question rather than settled here.
 */
export const SCHEDULE_READ: ReadonlySet<Role> = new Set<Role>([
  'owner',
  'head_coach',
  'assistant_coach',
  'team_manager',
  'guardian',
]);

export function canReadSchedule(roles: readonly Role[]): boolean {
  return roles.some((role) => SCHEDULE_READ.has(role));
}

const OPTIONAL_TEXT = z.string().trim().max(200).optional();

/**
 * The fields a coach fills in, before any cross-field rule.
 *
 * Split from the refined schema below because `.refine` returns a
 * ZodEffects, which cannot be `.partial()`ed for the update case. Keeping the
 * raw object shape separate means the create and update schemas share one
 * definition of every field instead of two that drift.
 */
const EventFields = z.object({
  type: EventTypeSchema,
  /**
   * Optional on purpose. A game against Lakewood does not need a coach to type
   * "vs Lakewood" to have a name, and asking them to is how you get a schedule
   * where half the rows say "Game". `eventTitle()` below derives the display
   * string, and both the web app and any export call it rather than each
   * inventing a fallback.
   */
  title: z.string().trim().max(120).optional(),
  opponent: z.string().trim().max(120).optional(),
  locationName: OPTIONAL_TEXT,
  locationAddress: z.string().trim().max(300).optional(),
  arrivalAt: TimestampSchema.optional(),
  startsAt: TimestampSchema,
  endsAt: TimestampSchema.optional(),
  uniform: UniformSchema.optional(),
  notes: z.string().trim().max(2000).optional(),
  /**
   * The date is known and the clock time is not. The row then holds noon in
   * `SCHEDULE_TIME_ZONE` on that date as `startsAt` (so ordering and the
   * arrival CHECK keep working), no arrival, and an `endsAt` only when the
   * event runs over more than one day.
   */
  timeIsTbd: z.boolean().optional(),
});

/**
 * The zone a schedule's calendar dates are read in.
 *
 * A time-TBD event is a DATE, and a date has to be anchored somewhere to
 * become the instant the database sorts by. The club this is built for is in
 * one town; a per-team zone is flagged in `datetime.ts` and not invented here.
 */
export const SCHEDULE_TIME_ZONE = 'America/New_York';

function zoneParts(instant: Date): Record<string, number> {
  const parts: Record<string, number> = {};
  for (const part of new Intl.DateTimeFormat('en-US', {
    timeZone: SCHEDULE_TIME_ZONE,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(instant)) {
    if (part.type !== 'literal') parts[part.type] = Number(part.value);
  }
  return parts;
}

/** The calendar date an instant falls on in the schedule zone, as `YYYY-MM-DD`. */
export function scheduleDateOf(iso: string): string {
  const p = zoneParts(new Date(iso));
  const pad = (value: number | undefined): string => String(value ?? 0).padStart(2, '0');
  return `${String(p['year'] ?? 0)}-${pad(p['month'])}-${pad(p['day'])}`;
}

/**
 * Noon in the schedule zone on a calendar date, as an ISO instant.
 *
 * Noon rather than midnight so that the instant never crosses into the
 * previous day in any zone a device is likely to be in, and so daylight
 * saving (which changes at 2am) cannot move it. The offset is read from
 * `Intl` for that date rather than assumed, because the same date is five
 * hours off UTC in winter and four in summer.
 */
export function tbdInstant(date: string): string {
  const [year = 0, month = 1, day = 1] = date.split('-').map(Number);
  const guess = Date.UTC(year, month - 1, day, 12);
  const p = zoneParts(new Date(guess));
  const wall = Date.UTC(
    p['year'] ?? 0,
    (p['month'] ?? 1) - 1,
    p['day'] ?? 1,
    p['hour'] ?? 0,
    p['minute'] ?? 0,
    p['second'] ?? 0,
  );
  return new Date(guess - (wall - guess)).toISOString();
}

/**
 * The canonical pair a time-TBD event stores: noon of the start date, and
 * noon of the LAST date only when that is a later day. A same-day end is no
 * end at all, so it is dropped rather than stored as a zero-length event.
 */
export function tbdTimes(
  startsAt: string,
  endsAt?: string | null,
): { readonly startsAt: string; readonly endsAt: string | null } {
  const start = scheduleDateOf(startsAt);
  const end = endsAt === undefined || endsAt === null ? null : scheduleDateOf(endsAt);
  return {
    startsAt: tbdInstant(start),
    endsAt: end === null || end <= start ? null : tbdInstant(end),
  };
}

/**
 * The instant after which an event has started, for "has it passed" rules.
 *
 * A timed event has started at its start. A time-TBD event has no start on
 * the clock, only a last day, so it counts as started at the END of that day:
 * noon plus twelve hours in the schedule zone. Refusing an RSVP at 12:01 on a
 * day whose time nobody has announced yet would be the alternative.
 */
export function effectiveStart(event: {
  readonly startsAt: string;
  readonly endsAt?: string | null;
  readonly timeIsTbd?: boolean | undefined;
}): number {
  if (event.timeIsTbd !== true) return Date.parse(event.startsAt);
  return Date.parse(event.endsAt ?? event.startsAt) + 12 * 60 * 60 * 1000;
}

/**
 * The cross-field rules, applied to whichever fields are present.
 *
 * Written against a partial shape so create and update can share it. On update
 * the caller merges the patch over the stored row FIRST and validates the
 * result, because checking a patch in isolation cannot see that moving
 * `startsAt` earlier just put it before an `arrivalAt` that was not in the
 * patch.
 */
type EventFieldValues = z.infer<typeof EventFields>;
/*
 * Written by hand rather than as `Partial<...>` because `tsconfig` sets
 * `exactOptionalPropertyTypes`. Under that flag `Partial<T>` means "the key may
 * be absent", not "the value may be undefined", and the refinement below is
 * handed the second shape by `.partial()`. They are different types and the
 * compiler is right to say so.
 */
type EventShape = { [K in keyof EventFieldValues]?: EventFieldValues[K] | null | undefined };

function order(value: string | null | undefined): number | undefined {
  // null means the caller is CLEARING the field, so there is nothing to order
  // against, exactly as an absent key means.
  if (value === undefined || value === null) return undefined;
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? undefined : ms;
}

export function checkEventRules(value: EventShape, ctx: z.RefinementCtx): void {
  const arrival = order(value.arrivalAt);
  const start = order(value.startsAt);
  const end = order(value.endsAt);

  /*
   * Arrival BEFORE OR AT the start, never after. Equal is allowed and is not a
   * mistake: a practice where you show up and start is the common case, and
   * rejecting it would make every coach type a fake arrival time.
   */
  if (arrival !== undefined && start !== undefined && arrival > start) {
    ctx.addIssue({
      code: 'custom',
      path: ['arrivalAt'],
      message: 'Arrival time is after the start time.',
    });
  }

  if (start !== undefined && end !== undefined && end < start) {
    ctx.addIssue({
      code: 'custom',
      path: ['endsAt'],
      message: 'End time is before the start time.',
    });
  }

  /*
   * A time to be announced has no arrival time: there is nothing to arrive
   * before. The client clears the arrival when it sets the flag; a request
   * that carries both is refused rather than quietly dropping one of them.
   */
  if (value.timeIsTbd === true && arrival !== undefined) {
    ctx.addIssue({
      code: 'custom',
      path: ['timeIsTbd'],
      message: 'A time to be announced has no arrival time.',
    });
  }

  const opponent = value.opponent;
  if (
    opponent !== undefined &&
    opponent !== null &&
    opponent !== '' &&
    value.type !== undefined &&
    value.type !== null &&
    !OPPONENT_TYPES.has(value.type)
  ) {
    ctx.addIssue({
      code: 'custom',
      path: ['opponent'],
      message: 'Only a game, scrimmage, or tournament has an opponent.',
    });
  }
}

export const EventCreateSchema = EventFields.extend({
  savedLocationId: z.uuid().optional(),
  /**
   * False saves a draft that only the schedule's writers can see. Absent
   * means published, which is what a coach adding a practice expects. NOT on
   * the update schema: publishing is its own audited action.
   */
  isPublished: z.boolean().optional(),
}).superRefine(checkEventRules);
export type EventCreate = z.infer<typeof EventCreateSchema>;

export const EventPublishSchema = z.object({ isPublished: z.boolean() });
export type EventPublish = z.infer<typeof EventPublishSchema>;

/**
 * An update is a patch. Every field is optional, including `type` and
 * `startsAt`, but an empty patch is refused rather than treated as a no-op
 * write: it would produce an audit entry and a change notification for nothing.
 */
/**
 * The fields an update may CLEAR, as distinct from leave alone.
 *
 * An absent key means "do not touch"; `null` means "remove this". Without the
 * second, a coach who typed the wrong arrival time could change it but never
 * take it off, and the screen had to refuse the empty box rather than accept a
 * save that would silently do nothing. Raised as a P2 by the cross-model pass
 * and independently by the screen's own author, which is a good sign it was
 * really a gap rather than a preference.
 *
 * `startsAt` and `type` are NOT clearable. An event with no start time cannot
 * be shown anywhere, and one with no type cannot be labelled.
 */
export const EventUpdateSchema = EventFields.partial()
  .extend({
    title: z.string().trim().max(120).nullish(),
    opponent: z.string().trim().max(120).nullish(),
    locationName: z.string().trim().max(200).nullish(),
    locationAddress: z.string().trim().max(300).nullish(),
    arrivalAt: TimestampSchema.nullish(),
    endsAt: TimestampSchema.nullish(),
    uniform: UniformSchema.nullish(),
    notes: z.string().trim().max(2000).nullish(),
  })
  .refine((patch) => Object.keys(patch).length > 0, {
    message: 'Nothing to change.',
  })
  .superRefine(checkEventRules);
export type EventUpdate = z.infer<typeof EventUpdateSchema>;

/**
 * Cancellation is a state, not a delete (BUILD_PLAN 4.5), and the reason is
 * required.
 *
 * A cancelled row with no reason is the thing the coach was trying to avoid:
 * every parent then texts to ask why. Uncancelling clears the reason, which is
 * why the reason is not required in that direction.
 */
export const EventCancelSchema = z.discriminatedUnion('isCancelled', [
  z.object({
    isCancelled: z.literal(true),
    cancellationReason: NonEmptyStringSchema.max(300),
  }),
  z.object({ isCancelled: z.literal(false) }),
]);
export type EventCancel = z.infer<typeof EventCancelSchema>;

export const EventSchema = EventFields.extend({
  id: UuidSchema,
  teamId: UuidSchema,
  seasonId: UuidSchema,
  isCancelled: z.boolean(),
  cancellationReason: z.string().nullable(),
  timeIsTbd: z.boolean(),
  isPublished: z.boolean(),
});
export type ScheduleEvent = z.infer<typeof EventSchema>;

const TYPE_LABEL: Record<EventType, string> = {
  game: 'Game',
  practice: 'Practice',
  tournament: 'Tournament',
  scrimmage: 'Scrimmage',
  team_event: 'Team event',
  other: 'Event',
};

export function eventTypeLabel(type: EventType): string {
  return TYPE_LABEL[type];
}

/**
 * The one place a schedule row's display name is decided.
 *
 * An explicit title always wins. Otherwise a game against a named opponent
 * reads "vs Lakewood", and everything else falls back to its type label. The
 * web app, an ICS feed, and an email digest all have to agree on this string
 * or the same event appears under two names in two places, so it lives beside
 * the schema rather than in a component.
 */
export function eventTitle(event: {
  type: EventType;
  title?: string | null;
  opponent?: string | null;
}): string {
  const title = event.title?.trim();
  if (title !== undefined && title !== '') return title;

  const opponent = event.opponent?.trim();
  if (opponent !== undefined && opponent !== '' && OPPONENT_TYPES.has(event.type)) {
    return `vs ${opponent}`;
  }

  return TYPE_LABEL[event.type];
}

/**
 * The notification rule from BUILD_PLAN 4.5, as a pure predicate.
 *
 * "Any edit to time, date, or location on an event within 72 hours triggers an
 * immediate notification regardless of digest preference." The window is
 * measured to the event's START, and `now` is a parameter rather than read from
 * the clock so it can be tested and so the caller decides what "now" means.
 *
 * A cancellation is not routed through here: BUILD_PLAN 4.5 and the carpool
 * rule in 4.10 both treat a cancellation as always-urgent, at any distance.
 */
export const URGENT_CHANGE_WINDOW_MS = 72 * 60 * 60 * 1000;

export function isUrgentChange(startsAt: string, now: Date): boolean {
  const start = Date.parse(startsAt);
  if (Number.isNaN(start)) return false;
  const until = start - now.getTime();
  /*
   * An event already in the past is not urgent. Editing last week's practice
   * to fix a typo must not page thirty families at 11pm.
   */
  return until >= 0 && until <= URGENT_CHANGE_WINDOW_MS;
}

/**
 * Field codes for the cross-field rules, as an API `FieldError` carries them.
 *
 * The envelope is `{ field, code }` and never a message: the SERVER does not
 * write user-facing copy, the client maps the code to themed copy. CLAUDE.md is
 * explicit that no raw error string reaches a user, and a `message` field on
 * the wire is the crack that rule leaks through.
 *
 * The Zod `message` on each rule above is still there and is still worth
 * having: it names the rule for anyone reading a log or a test failure.
 *
 * LOWERCASE, and that is load bearing rather than a style choice. `apiError`
 * validates every field code against `/^[a-z][a-z0-9_]*$/` and silently
 * replaces anything failing it with `invalid`, so an uppercase code never
 * reaches the wire at all. These were uppercase and were being stripped, which
 * a fresh-context pass caught: the comments on both sides described a mapping
 * that could not happen. The rest of the API spells them this way too
 * (`invalid_email`, `not_assignable`).
 */
export const EVENT_FIELD_CODES = {
  arrivalAt: 'arrival_after_start',
  endsAt: 'ends_before_starts',
  opponent: 'opponent_not_allowed',
  timeIsTbd: 'arrival_with_tbd',
} as const;

/**
 * Giving a time-TBD event its time needs the time. Raised on `startsAt` by
 * the repository, which alone can see the stored flag: a patch of
 * `{ timeIsTbd: false }` with no `startsAt` would otherwise publish the
 * placeholder noon as the start a coach chose. Beside `EVENT_FIELD_CODES`
 * rather than in it, because that table is what `eventFieldErrors` maps a
 * pathless Zod issue onto, and "nothing to change" is not "time required".
 */
export const EVENT_TIME_REQUIRED = 'time_required';

/**
 * Zod issues, as the API's `{ field, code }` pairs.
 *
 * A `custom` issue is one of the three cross-field rules and gets its specific
 * code. Anything else is a shape failure (a missing start time, a string that
 * is not a timestamp) and gets `INVALID`, because guessing which rule tripped
 * from a path alone is how "that is not a date" becomes "arrival is after the
 * start" in front of a coach who typed neither.
 */
export function eventFieldErrors(
  issues: readonly { code: string; path: PropertyKey[]; message?: string }[],
): { field: string; code: string }[] {
  return issues.map((issue) => {
    const field = String(issue.path[0] ?? 'startsAt');
    if (issue.code !== 'custom') return { field, code: 'invalid' };
    const known = (EVENT_FIELD_CODES as Record<string, string>)[field];
    return { field, code: known ?? 'invalid' };
  });
}

/**
 * Who may write to a scorebook.
 *
 * BUILD_PLAN section 3, read literally. Assistant Coach has "scorebook full
 * access". Scorekeeper has "Scorebook write for assigned games only", and the
 * per-game part of that is NOT expressed here, because this set answers "may
 * this role write a scorebook at all". The assignment half is NOT checked
 * anywhere in the product today: ADR-0048 accepts this as a known-open
 * scorekeeper over-grant rather than a per-game check made elsewhere.
 * Splitting it that way keeps the role table and this list comparable line
 * by line.
 *
 * Team Manager is absent, and the plan says so in as many words: "No scorebook
 * write." That is the one row where a reader might reasonably assume otherwise,
 * since a team manager writes almost everything else.
 *
 * Guardian is absent. A guardian reads "all published stats", which is a
 * different capability and a later phase.
 */
export const SCOREBOOK_WRITE: ReadonlySet<Role> = new Set<Role>([
  'owner',
  'head_coach',
  'assistant_coach',
  'scorekeeper',
]);

/**
 * Who may read a coach's NOTE events back out of the log: coaches, only.
 *
 * NOT `SCOREBOOK_WRITE`, and the difference is one role. A scorekeeper writes
 * the book, and BUILD_PLAN section 3 lets that role be a stranger holding a
 * texted magic link; a NOTE is a coach's freeform words about a child
 * (BUILD_PLAN marks coach development notes coach-only). The first version of
 * the log filter gated on write access, which handed every season note to
 * that stranger, under a comment claiming coach-only. Caught by the delta
 * pass. The scorekeeper who records a dictated note still holds it in their
 * own device's outbox; what they lose is read-back of the whole season's
 * notes, which is the right side to err on.
 */
export const SCOREBOOK_NOTE_READ: ReadonlySet<Role> = new Set<Role>([
  'owner',
  'head_coach',
  'assistant_coach',
]);

export function canReadCoachNotes(roles: readonly Role[]): boolean {
  return roles.some((role) => SCOREBOOK_NOTE_READ.has(role));
}

/*
 * Reopening a finished game is governed by `SCOREBOOK_WRITE` above, and there
 * is deliberately no separate set for it.
 *
 * There was one for a round, restricted to coaches, and it created the defect
 * it was supposed to avoid. Appending is a write, so a scorekeeper's phone can
 * hold plays that a finished game refuses; the refusal's copy says "Reopen it
 * to make a change"; and the role that received that instruction was the one
 * role forbidden from acting on it. Its plays could not land by any action
 * available to the person holding them, which is the loss this whole vertical
 * exists to prevent.
 *
 * Whoever can wall the book can unwall it. That is not a widening: the same
 * role already holds `finish`, which writes the league's official score, and
 * reopening is the weaker, restorative half of that pair. The over-grant that
 * lets a texted-link scorekeeper reach either one is ADR-0048, tracked there
 * and not re-litigated here.
 */

export function canWriteScorebook(roles: readonly Role[]): boolean {
  return roles.some((role) => SCOREBOOK_WRITE.has(role));
}

/**
 * Who may read a game's event log and box score.
 *
 * Everyone with a role on the team except the reserved one. A scorekeeper has
 * to read the log they are writing, a team manager runs the bench and needs the
 * score, and a guardian follows the game live.
 *
 * Membership here is not the whole story, and this comment used to claim it
 * was. The log route additionally drops NOTE events for roles outside
 * `SCOREBOOK_NOTE_READ` above, so what reaches anyone below a coach is the
 * play-by-play a parent in the stands already watched, minus the coach's
 * notes. The broader "published stats" surface (`games.public_enabled` and a
 * publish gate) is a later phase.
 *
 * `club_admin` is excluded here rather than throwing, because throwing is
 * `resolveMembership`'s job and it happens before any route runs. Listing it
 * would be dead code that reads like a decision.
 */
export const SCOREBOOK_READ: ReadonlySet<Role> = new Set<Role>([
  'owner',
  'head_coach',
  'assistant_coach',
  'team_manager',
  'scorekeeper',
  'guardian',
]);

export function canReadScorebook(roles: readonly Role[]): boolean {
  return roles.some((role) => SCOREBOOK_READ.has(role));
}

/**
 * Who may approve a submitted scorebook, which is what publishes it (ADR-0139).
 *
 * Owner, head coach, and assistant coach. A scorekeeper submits and reopens
 * (both stay on `SCOREBOOK_WRITE`) and does not approve. A literal set, pinned
 * by tests, so a new role does not publish a book by being added to the enum.
 *
 * `club_admin` is absent for the same reason it is absent from `SCOREBOOK_READ`:
 * the role throws before a route runs. A club owner or division head reaches
 * this set only by acting as `owner` or `head_coach` (ADR-0127), which is the
 * membership the request already carries.
 */
export const SCOREBOOK_APPROVE: ReadonlySet<Role> = new Set<Role>([
  'owner',
  'head_coach',
  'assistant_coach',
]);

export function canApproveScorebook(roles: readonly Role[]): boolean {
  return roles.some((role) => SCOREBOOK_APPROVE.has(role));
}

/**
 * Who may read a scorebook that a coach has not approved yet.
 *
 * The same four roles that can write one. A team manager and a guardian see
 * nothing of the book until `approvedAt` is set, including while the game is
 * being scored. That reverses ADR-0055. The set is literal and pinned.
 */
export const SCOREBOOK_UNPUBLISHED_READ: ReadonlySet<Role> = new Set<Role>([
  'owner',
  'head_coach',
  'assistant_coach',
  'scorekeeper',
]);

export function canReadUnpublishedBook(roles: readonly Role[]): boolean {
  return roles.some((role) => SCOREBOOK_UNPUBLISHED_READ.has(role));
}

/**
 * Whether this role may see this game's book.
 *
 * One predicate. A published book (`approvedAt` set) is visible to every role
 * the caller passes. An unpublished book is visible only to
 * `SCOREBOOK_UNPUBLISHED_READ`. The route still refuses a role outside
 * `SCOREBOOK_READ` before this runs, so publishing does not hand the book to
 * `club_admin`.
 */
export function bookVisibleTo(
  roles: readonly Role[],
  game: { readonly approvedAt: string | null },
): boolean {
  return game.approvedAt !== null || canReadUnpublishedBook(roles);
}

export const SchedulePreferencesSchema = z.object({
  start: z.number().int().min(0).max(1439),
  duration: z.number().int().min(1).max(1440),
  arrival: z.number().int().min(0).max(60),
  location: z.string(),
});
export type SchedulePreferences = z.infer<typeof SchedulePreferencesSchema>;
export const TeamSchedulePreferencesSchema = z.partialRecord(
  EventTypeSchema,
  SchedulePreferencesSchema,
);
